let globalToastFn = null

// Corpo de erro do back (DRF) é objeto; HTML/texto vem de camadas antes dele (ex.: WAF).
const isErrorObject = data => !!data && typeof data === 'object' && !Array.isArray(data)

// Chaves que o custom_exception_handler do back injeta em todo erro — não são
// campo de formulário. Erro de campo vem na raiz e repetido em `errors`; erro
// geral (ServiceException, throttling) vem só como `detail`/`non_field_errors`.
const ERROR_METADATA_KEYS = ['detail', 'errors', 'context', 'status_code', 'non_field_errors']

// Página de bloqueio da Cloudflare: "Cloudflare Ray ID: <strong>8f1a…</strong>".
// O header cf-ray não é legível pelo front (CORS), o HTML é.
const extractCloudflareRayId = html =>
  typeof html === 'string' ? html.match(/Ray ID:?\s*(?:<[^>]*>\s*)*([0-9a-f]{16})/i)?.[1] : null

const rejectedBeforeProcessingMessage = data => {
  const rayId = extractCloudflareRayId(data)
  const contact = rayId
    ? `entre em contato com o suporte informando o código ${rayId}.`
    : 'entre em contato com o suporte.'
  return `A solicitação foi recusada antes de ser processada. Se o problema continuar, ${contact}`
}

export function registerToast(fn) {
  globalToastFn = fn
}

export function showToast(message, options = {}) {
  if (typeof globalToastFn === 'function') {
    globalToastFn(message, options)
  }
}

/**
 * Extrai as mensagens de erro de uma resposta da API.
 * Pura (sem DOM/toast) para ser testável isoladamente.
 *
 * Ordem: objeto `errors` do backend (achatado em lista) → `detail` → falha de rede
 * do axios (sem response) → response sem JSON → fallback genérico. Response sem JSON
 * vem de antes do back: 4xx (WAF da Cloudflare, 413 do nginx) não passa repetindo,
 * então orienta o suporte; 5xx (502/504 do proxy) pede nova tentativa.
 *
 * @param {object} [error] Erro do axios.
 * @param {string} [fallback] Mensagem usada quando nada acima se aplica; cada tela
 *   passa a sua. Sem ele, 'Erro desconhecido'.
 */
export function extractApiErrorMessages(error, fallback = 'Erro desconhecido') {
  const data = error?.response?.data

  const errors = data?.errors
  if (errors && typeof errors === 'object') {
    const messages = Object.values(errors)
      .flat()
      .filter(message => typeof message === 'string' && message)
    if (messages.length) return messages
  }

  if (typeof data?.detail === 'string' && data.detail) return [data.detail]

  if (error?.isAxiosError && !error.response) {
    return ['Não foi possível se comunicar com o servidor. Tente novamente em instantes.']
  }

  if (error?.response && !isErrorObject(data)) {
    if (error.response.status >= 400 && error.response.status < 500) {
      return [rejectedBeforeProcessingMessage(data)]
    }
    return ['Não foi possível concluir a solicitação. Tente novamente em instantes.']
  }

  return [fallback]
}

/**
 * Exibe um toast de erro a partir de um erro de API (sucessor do antigo
 * composable useApiError, que disparava Swal).
 */
export function showApiError(error, { title } = {}) {
  showToast(extractApiErrorMessages(error).join(' · '), {
    variant: 'error',
    ...(title ? { title } : {}),
  })
}

/**
 * Repassa os erros de campo da API ao formulário (vee-validate `setErrors`) e
 * mostra em toast o que não tem campo onde aparecer: falha de rede, corpo que não
 * é objeto de erros (HTML do WAF), erro geral do back (`detail`) e — com `fields`
 * — erros de campos que o formulário não registra (o `setErrors` os ignoraria
 * sem avisar).
 *
 * @param {object} [options]
 * @param {string} [options.title] Título do toast.
 * @param {string[]} [options.fields] Campos do formulário. Sem ele, toda chave de
 *   campo vai ao `setErrors`.
 */
export function applyApiErrors(error, setErrors, { title, fields } = {}) {
  const data = error?.response?.data
  if (!isErrorObject(data)) {
    showApiError(error, { title })
    return
  }

  const fieldEntries = Object.entries(data).filter(([key]) => !ERROR_METADATA_KEYS.includes(key))
  const formErrors = fields ? fieldEntries.filter(([key]) => fields.includes(key)) : fieldEntries
  const unmappedErrors = fields ? fieldEntries.filter(([key]) => !fields.includes(key)) : []

  if (!formErrors.length) {
    showApiError(error, { title })
    return
  }

  setErrors(Object.fromEntries(formErrors))
  if (unmappedErrors.length) {
    showApiError({ response: { data: { errors: Object.fromEntries(unmappedErrors) } } }, { title })
  }
}
